import { Check, Flag, Maximize2, RefreshCw, Trash2, WandSparkles } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";


import { Heading6, LabelText, SmallText, XsmallText } from "../../lib/ui/components/typography/Typography";
import Button from "../../lib/ui/components/button/Button";
import Spinner from "../../lib/ui/components/spinner/Spinner";
import Input from "../../lib/ui/components/input/Input";
import Textarea from "../../lib/ui/components/input/Textarea";
import Modal from "../../lib/ui/components/Modal";
import { ANALYSIS_RULES, GENERATE_ANALYSIS_CRITERIA } from "../../lib/urls";
import {
    OVERALL_RATING_PLACEHOLDERS,
    SCORES,
    analysisCriteriaToDraft,
    draftToAnalysisCriteria,
    newRubricId,
} from "./analysisRubricDraft";
import SectionFooter from "../../lib/ui/components/section-footer/SectionFooter";

const SCORE_BADGE_CLASSES = {
    5: "bg-green-100 text-green-800",
    4: "bg-emerald-50 text-green-700",
    3: "bg-yellow-100 text-yellow-800",
    2: "bg-orange-100 text-orange-700",
    1: "bg-red-100 text-red-700",
};

const ScoreBadge = ({ score }) => (
    <span className={`flex items-center justify-center w-7 h-7 shrink-0 rounded-md text-body font-bold ${SCORE_BADGE_CLASSES[score]}`}>
        {score}
    </span>
);

const SectionHeader = ({ title, subtitle, action }) => (
    <div className="flex items-baseline gap-2 mb-3">
        <SmallText weight="bold" emphasis={true}>{title}</SmallText>
        {subtitle && <XsmallText>{subtitle}</XsmallText>}
        {action && <div className="ml-auto">{action}</div>}
    </div>
);

const LinkButton = ({ children, ...props }) => (
    <button
        type="button"
        className="text-body font-medium text-primary hover:underline disabled:opacity-50"
        {...props}
    >
        {children}
    </button>
);

const AnalysisRubric = ({
    roleInputs,
    rubric,
    onRubricChange,
    onSaveRoleDetails,
    onGenerateInterviewQuestions,
    hasInterviewQuestions = false,
    onContinue,
    onBack,
    companyId,
    jobId,
    token,
    isGeneratingRules = false,
    footerHost,
}) => {
    const roleInputsKey = JSON.stringify(roleInputs);
    const [generatedFrom, setGeneratedFrom] = useState(rubric ? roleInputsKey : null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [savingAction, setSavingAction] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [expandedScore, setExpandedScore] = useState(null);
    const rubricIdentity = rubric
        ? `${rubric.id ?? "new"}:${rubric.version ?? "unversioned"}`
        : null;
    const previousRubricIdentityRef = useRef(rubricIdentity);

    useEffect(() => {
        if (previousRubricIdentityRef.current === rubricIdentity) return;
        previousRubricIdentityRef.current = rubricIdentity;
        if (rubricIdentity && !isDirty) setGeneratedFrom(roleInputsKey);
    }, [rubricIdentity, isDirty, roleInputsKey]);

    const isStale = Boolean(rubric) && generatedFrom !== roleInputsKey;
    const generating = isGenerating || isGeneratingRules;

    const updateRubric = (changes) => {
        onRubricChange({ ...rubric, ...changes });
        setIsDirty(true);
    };

    const generateRubric = async () => {
        setIsGenerating(true);
        try {
            await onSaveRoleDetails();
            const response = await fetch(GENERATE_ANALYSIS_CRITERIA(companyId, jobId), {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(data.detail || "Could not generate evaluation rules");
            const draft = analysisCriteriaToDraft(data);
            onRubricChange(draft);
            setGeneratedFrom(roleInputsKey);
            setIsDirty(false);
            toast.success("Evaluation rules generated");
        } catch (error) {
            toast.error(error.message || "Could not generate evaluation rules");
        } finally {
            setIsGenerating(false);
        }
    };

    const saveRubric = async ({ generateQuestions = false } = {}) => {
        setSavingAction(generateQuestions ? "generate-questions" : "save");
        setIsSaving(true);
        try {
            const response = await fetch(ANALYSIS_RULES(companyId, jobId), {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(draftToAnalysisCriteria(rubric)),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(data.detail || "Could not save evaluation rules");
            onRubricChange(analysisCriteriaToDraft(data));
            setGeneratedFrom(roleInputsKey);
            setIsDirty(false);
            toast.success("Evaluation rules saved");
            if (generateQuestions) onGenerateInterviewQuestions?.();
        } catch (error) {
            toast.error(error.message || "Could not save evaluation rules");
        } finally {
            setIsSaving(false);
            setSavingAction(null);
        }
    };

    const updateRule = (key, id, changes) =>
        updateRubric({ [key]: rubric[key].map((rule) => (rule.id === id ? { ...rule, ...changes } : rule)) });

    const removeRule = (key, id) => updateRubric({ [key]: rubric[key].filter((rule) => rule.id !== id) });

    const questionsAlreadyExist = hasInterviewQuestions && !isDirty;
    const continueToQuestions = () => {
        if (questionsAlreadyExist) {
            onContinue?.();
            return;
        }
        if (isDirty) {
            saveRubric({ generateQuestions: true });
            return;
        }
        onGenerateInterviewQuestions?.();
    };
    const continueLabel = savingAction === "generate-questions"
        ? "Saving..."
        : isDirty
            ? "Save & Generate Interview Questions"
            : questionsAlreadyExist
                ? "Continue"
                : "Generate Interview Questions";

    return (
        <div className="w-full text-black" data-cy="analysis-rubric">
            <div className="mb-3">
                <LabelText weight="normal" className="!mb-0">Evaluation Rules</LabelText>
            </div>
              <div className="flex flex-col gap-3">
              <XsmallText className="leading-[150%] text-gray-600">
                  Each question carries its own scoring scale in the Interview view. These rules sit on top of those scores.
              </XsmallText>

              {!rubric || isGeneratingRules ? (
                isGeneratingRules ? (
                <div
                    className="flex flex-col items-center rounded-md border border-gray-200 bg-white px-4 py-16 text-center"
                    data-cy="evaluation-rules-loading"
                >
                    <Spinner size="xl" />
                    <Heading6 emphasis={false} className="mt-4">Generating evaluation rules</Heading6>
                </div>
                ) : (
                <div className="rounded-md border border-gray-200 bg-white p-4 text-center py-12">
                    <Heading6 emphasis={false}>No evaluation rules have been generated yet.</Heading6>
                    <XsmallText className="mt-2">
                        {roleInputs.description
                            ? "Fill in “What does a great hire look like?” and “Dealbreakers” for sharper rules, then generate."
                            : "Add a role description first."}
                    </XsmallText>
                </div>
                )
            ) : (
                <div className="flex flex-col gap-3">
                    {isStale && (
                        <div className="flex items-center gap-3 px-3 py-3 bg-amber-50 border border-amber-200 rounded-md" data-cy="rubric-stale-banner">
                            <RefreshCw size="1em" className="text-amber-700 shrink-0" />
                            <XsmallText className="flex-1 !text-amber-900">
                                <strong>Role inputs changed since these rules were generated.</strong> Your edits are kept unless you regenerate.
                            </XsmallText>
                            <Button variant="secondary" onClick={() => setGeneratedFrom(roleInputsKey)}>
                                Keep current
                            </Button>
                            <Button variant="primary" onClick={generateRubric} disabled={generating}>
                                Regenerate
                            </Button>
                        </div>
                    )}

                    <div className="flex flex-col gap-3">
                        <div className="rounded-md border border-gray-200 bg-white p-4">
                            <SectionHeader title="Score caps" />
                            <XsmallText className="-mt-2 mb-3">Hard ceilings on the overall rating</XsmallText>
                            <div className="flex flex-col gap-2">
                                {rubric.caps.map((cap) => (
                                    <div key={cap.id} className="flex items-center gap-2">
                                        <Textarea
                                            value={cap.rule}
                                            onChange={(e) => updateRule("caps", cap.id, { rule: e.target.value })}
                                            placeholder="If the candidate…"
                                            aria-label="Cap rule"
                                            rows={2}
                                            className="flex-1 min-w-0 resize-none"
                                        />
                                        <select
                                            value={cap.max}
                                            onChange={(e) => updateRule("caps", cap.id, { max: Number(e.target.value) })}
                                            aria-label="Maximum score"
                                            className="text-body font-semibold text-red-700 bg-red-50 border border-red-200 rounded-md py-1 pl-2 pr-7"
                                        >
                                            {[1, 2, 3, 4].map((max) => (
                                                <option key={max} value={max}>Max {max}</option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            onClick={() => removeRule("caps", cap.id)}
                                            aria-label="Remove cap"
                                            className="text-gray-400 hover:text-red-600"
                                        >
                                            <Trash2 size="1em" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-3">
                                <LinkButton
                                    onClick={() => updateRubric({ caps: [...rubric.caps, { id: newRubricId("cap"), rule: "", max: 2 }] })}
                                >
                                    + Add cap
                                </LinkButton>
                            </div>
                        </div>

                        <div className="rounded-md border border-gray-200 bg-white p-4" data-cy="bypass-rules">
                            <SectionHeader title="Bypass rules" />
                            <XsmallText className="-mt-2 mb-3">Flag the candidate for review whatever they score</XsmallText>
                            <div className="flex flex-col gap-2">
                                {rubric.bypasses.map((bypass) => (
                                    <div key={bypass.id} className="flex items-center gap-2">
                                        <Input
                                            value={bypass.rule}
                                            onChange={(e) => updateRule("bypasses", bypass.id, { rule: e.target.value })}
                                            placeholder="If the candidate…"
                                            aria-label="Bypass rule"
                                            className="flex-1 min-w-0"
                                        />
                                        <span className="flex items-center gap-1 text-body font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 whitespace-nowrap">
                                            <Flag size="1em" />
                                            Review
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => removeRule("bypasses", bypass.id)}
                                            aria-label="Remove bypass rule"
                                            className="text-gray-400 hover:text-red-600"
                                        >
                                            <Trash2 size="1em" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-3">
                                <LinkButton
                                    onClick={() => updateRubric({ bypasses: [...rubric.bypasses, { id: newRubricId("bypass"), rule: "" }] })}
                                >
                                    + Add bypass rule
                                </LinkButton>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-md border border-gray-200 bg-white p-4">
                        <SectionHeader title="Overall rating" subtitle="Context for how question scores roll up to the match rating" />
                        <div className="flex flex-col gap-2">
                            {SCORES.map((score) => (
                                <div key={score} className="flex flex-col gap-2 rounded-md border border-gray-200 bg-gray-50 p-3">
                                    <div className="flex items-center gap-2">
                                        <ScoreBadge score={score} />
                                        <input
                                            value={rubric.overall[score].label}
                                            onChange={(e) => updateRubric({ overall: { ...rubric.overall, [score]: { ...rubric.overall[score], label: e.target.value } } })}
                                            aria-label={`Label for ${score}`}
                                            placeholder={OVERALL_RATING_PLACEHOLDERS[score].label}
                                            className="min-w-0 flex-1 text-small font-semibold bg-transparent border border-transparent rounded-md px-1 hover:border-slate-200 focus:outline-none focus:border-slate-400"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setExpandedScore(score)}
                                            aria-label={`Expand overall rating ${score}`}
                                            title="View full rating guidance"
                                            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-white hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600"
                                        >
                                            <Maximize2 size="1em" aria-hidden />
                                        </button>
                                    </div>
                                    <Textarea
                                        value={rubric.overall[score].text}
                                        onChange={(e) => updateRubric({ overall: { ...rubric.overall, [score]: { ...rubric.overall[score], text: e.target.value } } })}
                                        aria-label={`Description for ${score}`}
                                        placeholder={OVERALL_RATING_PLACEHOLDERS[score].text}
                                        rows={4}
                                        background="white"
                                        className="resize-none"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
              )}
              </div>
            <SectionFooter
                host={footerHost}
                leading={
                    <Button variant="ghost" onClick={onBack} disabled={isSaving || generating} data-cy="evaluation-rules-back-button">
                        Back
                    </Button>
                }
            >
                <Button
                    variant="tertiary"
                    onClick={generateRubric}
                    disabled={generating || isSaving || !roleInputs.description}
                    leftIcon={generating ? <RefreshCw size="1em" /> : <WandSparkles size="1em" />}
                    data-cy="generate-rubric-button"
                >
                    {generating ? "Generating..." : rubric ? "Regenerate Rules" : "Generate Rules"}
                </Button>
                <Button
                    variant="secondary"
                    onClick={() => saveRubric()}
                    disabled={!rubric || !isDirty || isSaving || generating}
                    data-cy="save-rubric-button"
                >
                    {savingAction === "save" ? "Saving..." : "Save"}
                </Button>
                <Button
                    variant="primary"
                    onClick={continueToQuestions}
                    disabled={!rubric || isSaving || generating || !roleInputs.description}
                    leftIcon={<Check size="1em" />}
                    data-cy="save-and-generate-questions-button"
                >
                    {continueLabel}
                </Button>
            </SectionFooter>

            <Modal
                show={expandedScore !== null}
                onClose={() => setExpandedScore(null)}
                size="2xl"
                dismissible
            >
                <Modal.Header>Overall rating {expandedScore}</Modal.Header>
                <Modal.Body className="max-h-[70vh] overflow-y-auto">
                    {expandedScore !== null && (
                        <div className="flex items-start gap-4">
                            <ScoreBadge score={expandedScore} />
                            <div className="min-w-0 flex-1">
                                <h3 className="text-body-lg font-semibold text-gray-900">
                                    {rubric?.overall?.[expandedScore]?.label ||
                                        OVERALL_RATING_PLACEHOLDERS[expandedScore].label}
                                </h3>
                                <label
                                    htmlFor={`expanded-overall-rating-${expandedScore}`}
                                    className="mt-4 block text-small font-semibold text-gray-700"
                                >
                                    Rating criteria
                                </label>
                                <Textarea
                                    id={`expanded-overall-rating-${expandedScore}`}
                                    value={rubric?.overall?.[expandedScore]?.text || ""}
                                    onChange={(event) => updateRubric({
                                        overall: {
                                            ...rubric.overall,
                                            [expandedScore]: {
                                                ...rubric.overall[expandedScore],
                                                text: event.target.value,
                                            },
                                        },
                                    })}
                                    placeholder={OVERALL_RATING_PLACEHOLDERS[expandedScore].text}
                                    rows={10}
                                    className="mt-2 min-h-56 resize-y"
                                />
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setExpandedScore(null)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default React.memo(AnalysisRubric);
